// Data service for managing content from Google Sheets
class DataService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 15 * 60 * 1000; // 15 minutes cache timeout
    this.pendingRequests = new Map(); // Map to store pending promises
  }

  // Enhanced type detection method
  detectContentType(originalType, link) {
    if (!link) return originalType || 'PDF';

    // YouTube video detection
    if (link.includes('youtube.com') || link.includes('youtu.be')) {
      return 'Video';
    }

    // Audio file detection
    if (link.includes('.mp3') || link.includes('.wav') || link.includes('.m4a') || link.includes('.ogg')) {
      return 'Audio';
    }

    // PDF detection
    if (link.includes('.pdf') || (link.includes('drive.google.com') && !link.includes('.mp3') && !link.includes('.wav'))) {
      return 'PDF';
    }

    // Video file detection
    if (link.includes('.mp4') || link.includes('.avi') || link.includes('.mov') || link.includes('.mkv')) {
      return 'Video';
    }

    // Return original type if no specific detection
    return originalType || 'PDF';
  }

  // Get content for a specific batch
  async getContent(batch = '44') {
    const cacheKey = `content_${batch}`;

    // Check cache first
    const cachedData = this.cache.get(cacheKey);
    if (cachedData && Date.now() - cachedData.timestamp < this.cacheTimeout) {
      return cachedData.data;
    }

    // Check if there's already a pending request for this batch
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create a new request promise
    const requestPromise = (async () => {
      try {
        let content = [];

        // Try to fetch from Google Sheets first
        if (this.isGoogleSheetsConfigured()) {
          content = await this.fetchFromGoogleSheets(batch);
        } else {
          console.log('Google Sheets not configured, using demo data');
          // Fallback to demo data
          content = await this.fetchDemoData(batch);
        }

        // Cache the data
        this.cache.set(cacheKey, {
          data: content,
          timestamp: Date.now()
        });

        return content;
      } catch (error) {
        console.error('Error fetching content:', error);
        // Fallback to demo data on error
        return this.fetchDemoData(batch);
      } finally {
        // Remove from pending requests when done
        this.pendingRequests.delete(cacheKey);
      }
    })();

    // Store the promise
    this.pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  }

  // Check if Google Sheets is configured
  isGoogleSheetsConfigured() {
    const sheetId = process.env.REACT_APP_GOOGLE_SHEET_ID;
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

    return sheetId && apiKey &&
      sheetId !== 'your_google_sheet_id_here' &&
      apiKey !== 'your_google_api_key_here';
  }

  // Fetch from Google Sheets
  async fetchFromGoogleSheets(batch = '44') {
    const sheetId = process.env.REACT_APP_GOOGLE_SHEET_ID;
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    const range = 'Sheet1!A:O'; // Sheet1 tab, columns A to O (15 columns total)

    // Debug logging
    console.log('=== FETCHING FROM GOOGLE SHEETS ===');
    console.log('Sheet ID configured:', sheetId ? 'YES' : 'NO');
    console.log('API Key configured:', apiKey ? 'YES' : 'NO');
    console.log('Batch:', batch);

    const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}?key=${apiKey}`;

    const response = await fetch(url);

    if (!response.ok) {
      console.error('Google Sheets API error:', response.status, response.statusText);
      throw new Error(`Google Sheets API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('Number of rows received:', data.values ? data.values.length : 0);

    if (!data.values || data.values.length < 2) {
      console.log('No data found or only headers present');
      return [];
    }

    // Based on user screenshot, headers are in row 1, data starts from row 2
    const rows = data.values.slice(1);
    console.log('Data rows (excluding header):', rows.length);

    const allItems = [];

    rows
      .filter(row => row.length >= 12) // Ensure minimum columns for your sheet structure
      .forEach((row, index) => {
        // Based on your Google Sheets structure:
        // title | subject | college | level | language | batch | video_link | video_size | audio_link | audio_size | pdf_link | pdf_size | uploadstatus | description | uploaddate

        const videoLink = row[6] || ''; // Column G - video_link
        const audioLink = row[8] || ''; // Column I - audio_link  
        const pdfLink = row[10] || ''; // Column K - pdf_link

        // Base item properties
        const baseItem = {
          title: row[0] || '', // Column A - title
          batch: row[5] || batch, // Column F - batch
          subject: row[1] || 'General', // Column B - subject
          college: row[2] || 'Medical College', // Column C - college
          level: row[3] || 'Undergraduate', // Column D - level
          language: row[4] || 'en', // Column E - language
          uploadStatus: row[12] || 'Pending', // Column M - uploadstatus
          description: row[13] || '', // Column N - description
          uploadDate: row[14] || new Date().toISOString().split('T')[0], // Column O - uploaddate
          tags: []
        };

        // Create single item with all available content types
        const primaryType = this.detectContentType(null, videoLink || audioLink || pdfLink);
        const primaryLink = videoLink || audioLink || pdfLink;
        const primarySize = videoLink ? row[7] : audioLink ? row[9] : row[11];

        if (primaryLink) {
          allItems.push({
            ...baseItem,
            id: `${batch}_${index}`,
            type: primaryType,
            link: primaryLink,
            fileSize: primarySize || '',
            // Include all available links
            video_link: videoLink,
            video_size: row[7] || '',
            audio_link: audioLink,
            audio_size: row[9] || '',
            pdf_link: pdfLink,
            pdf_size: row[11] || ''
          });
        }
      });

    console.log('Total items before filtering:', allItems.length);

    // Log first few items for debugging
    if (allItems.length > 0) {
      console.log('Sample item:', allItems[0]);
    }

    const filteredItems = allItems
      .filter(item => {
        // Handle batch matching - your sheet has batch as "44"
        const trimmedBatch = item.batch ? item.batch.trim() : '';
        const batchMatch = trimmedBatch === batch ||
          trimmedBatch === `Batch ${batch}` ||
          trimmedBatch === batch.toString();

        // Handle upload status - your sheet has "Uploaded" 
        const statusMatch = item.uploadStatus === 'Uploaded' || item.uploadStatus === 'Success';

        // Ensure required fields are not empty
        const hasRequiredData = item.title && item.link;

        // Debug logging for filtered out items
        if (!batchMatch || !statusMatch || !hasRequiredData) {
          console.log(`Item filtered out: "${item.title}" - batch: ${item.batch} (match: ${batchMatch}), status: ${item.uploadStatus} (match: ${statusMatch}), hasData: ${hasRequiredData}`);
        }

        return batchMatch && statusMatch && hasRequiredData;
      });

    console.log('Items after filtering:', filteredItems.length);
    return filteredItems;
  }

  // Fetch demo data
  async fetchDemoData(batch = '44') {
    try {
      // Import demo data dynamically
      const demoModule = await import('../data/demoData.js');
      const demoData = demoModule.default || demoModule.demoData;

      if (!Array.isArray(demoData)) {
        console.error('Demo data is not an array');
        return [];
      }

      const filteredData = demoData
        .filter(item => {
          const isMatchingBatch = item.batch === batch;
          const isUploaded = item.uploadStatus === 'Uploaded';
          return isMatchingBatch && isUploaded;
        })
        .map(item => {
          // Add specific link properties based on content type
          const processedItem = {
            ...item,
            id: item.id || `demo_${Math.random().toString(36).substr(2, 9)}`,
            video_link: item.type === 'Video' ? item.link : '',
            audio_link: item.type === 'Audio' ? item.link : '',
            pdf_link: item.type === 'PDF' ? item.link : '',
            video_size: item.type === 'Video' ? item.fileSize : '',
            audio_size: item.type === 'Audio' ? item.fileSize : '',
            pdf_size: item.type === 'PDF' ? item.fileSize : ''
          };
          return processedItem;
        });

      return filteredData;
    } catch (error) {
      console.error('Error fetching demo data:', error);
      return [];
    }
  }

  // Get references from Google Sheets or demo data
  async getReferences() {
    const cacheKey = 'references';

    // Check cache first
    const cachedData = this.cache.get(cacheKey);
    if (cachedData && Date.now() - cachedData.timestamp < this.cacheTimeout) {
      return cachedData.data;
    }

    // Check if there's already a pending request
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create a new request promise
    const requestPromise = (async () => {
      try {
        let references = [];

        // Try to fetch from Google Sheets first
        if (this.isGoogleSheetsConfigured()) {
          references = await this.fetchReferencesFromGoogleSheets();
        } else {
          console.log('Google Sheets not configured, using demo references');
          references = await this.fetchDemoReferences();
        }

        // Cache the data
        this.cache.set(cacheKey, {
          data: references,
          timestamp: Date.now()
        });

        return references;
      } catch (error) {
        console.error('Error fetching references:', error);
        return this.fetchDemoReferences();
      } finally {
        this.pendingRequests.delete(cacheKey);
      }
    })();

    this.pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  // Fetch references from Google Sheets
  async fetchReferencesFromGoogleSheets() {
    const sheetId = process.env.REACT_APP_GOOGLE_SHEET_ID;
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    const range = 'References!A:M'; // References tab, adjust range as needed

    const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}?key=${apiKey}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Google Sheets API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.values || data.values.length < 2) {
      return [];
    }

    // Skip first row (headers)
    const rows = data.values.slice(1);

    return rows
      .filter(row => row.length >= 8) // Ensure minimum columns
      .map((row, index) => ({
        id: `ref_${index}`,
        title: row[0] || '',
        authors: row[1] || '',
        type: row[2] || 'Article',
        subject: row[3] || 'General Medicine',
        journal: row[4] || '',
        year: row[5] || new Date().getFullYear().toString(),
        language: row[6] || 'en',
        link: row[7] || '',
        doi: row[8] || '',
        abstract: row[9] || '',
        keywords: row[10] ? row[10].split(',').map(k => k.trim()) : [],
        category: row[11] || 'research',
        uploadDate: row[12] || new Date().toISOString().split('T')[0]
      }))
      .filter(item => {
        // Ensure required fields are not empty
        return item.title && item.authors;
      });
  }

  // Fetch demo references data
  async fetchDemoReferences() {
    return [
      {
        id: 'ref_1',
        title: 'Gastrointestinal Anatomy and Physiology',
        authors: 'Dr. Ahmed Hassan, Dr. Sarah Johnson',
        type: 'Textbook',
        subject: 'GIT 1',
        journal: 'Medical Education Press',
        year: '2024',
        language: 'en',
        link: 'https://drive.google.com/file/d/1VdqTYsIm1G5GWZ2Po1ebvDB02Uk8dM79/view',
        doi: '10.1234/git.2024.001',
        abstract: 'A comprehensive guide to gastrointestinal anatomy and physiology covering the digestive system structure and function.',
        keywords: ['GIT', 'anatomy', 'physiology', 'digestive system'],
        category: 'GIT 1',
        uploadDate: '2024-01-15'
      },
      {
        id: 'ref_2',
        title: 'GIT Diseases and Treatment Approaches',
        authors: 'Dr. Mohamed Elshafei, Dr. Lisa Chen',
        type: 'Research Article',
        subject: 'GIT 2',
        journal: 'Journal of Gastroenterology',
        year: '2024',
        language: 'en',
        link: 'https://drive.google.com/file/d/1VdqTYsIm1G5GWZ2Po1ebvDB02Uk8dM80/view',
        doi: '10.1234/git.2024.002',
        abstract: 'Latest advances in gastrointestinal disease diagnosis and treatment including interventional techniques.',
        keywords: ['GIT', 'diseases', 'treatment', 'gastroenterology'],
        category: 'GIT 2',
        uploadDate: '2024-01-20'
      },
      {
        id: 'ref_3',
        title: 'Fundamental Medical Sciences',
        authors: 'Dr. Fatima Al-Rashid, Dr. David Wilson',
        type: 'Review',
        subject: 'Basic',
        journal: 'Basic Medical Sciences Journal',
        year: '2024',
        language: 'en',
        link: 'https://drive.google.com/file/d/1VdqTYsIm1G5GWZ2Po1ebvDB02Uk8dM81/view',
        doi: '10.1234/basic.2024.003',
        abstract: 'Comprehensive review of fundamental concepts in medical sciences including biochemistry, cell biology, and genetics.',
        keywords: ['basic sciences', 'biochemistry', 'cell biology', 'genetics'],
        category: 'Basic',
        uploadDate: '2024-01-25'
      },
      {
        id: 'ref_4',
        title: 'Upper GIT Pathology',
        authors: 'Dr. Hassan Ahmad, Dr. Jennifer Brown',
        type: 'Clinical Guidelines',
        subject: 'GIT 1',
        journal: 'Clinical Gastroenterology Review',
        year: '2024',
        language: 'en',
        link: 'https://drive.google.com/file/d/1VdqTYsIm1G5GWZ2Po1ebvDB02Uk8dM82/view',
        doi: '10.1234/git.2024.004',
        abstract: 'Guidelines for understanding upper GIT pathology including esophagus, stomach and duodenum disorders.',
        keywords: ['upper GIT', 'pathology', 'clinical practice'],
        category: 'GIT 1',
        uploadDate: '2024-02-01'
      },
      {
        id: 'ref_5',
        title: 'Lower GIT Disorders: Diagnosis and Management',
        authors: 'Dr. Omar Khalil, Dr. Rebecca Martinez',
        type: 'Textbook',
        subject: 'GIT 2',
        journal: 'Gastroenterology Publications',
        year: '2024',
        language: 'en',
        link: 'https://drive.google.com/file/d/1VdqTYsIm1G5GWZ2Po1ebvDB02Uk8dM83/view',
        doi: '10.1234/git.2024.005',
        abstract: 'Detailed guide to identifying and treating lower GIT disorders including colon and rectum diseases.',
        keywords: ['lower GIT', 'colon', 'rectum', 'treatment'],
        category: 'GIT 2',
        uploadDate: '2024-02-05'
      },
      {
        id: 'ref_6',
        title: 'Medical Biochemistry Essentials',
        authors: 'Dr. Layla Ibrahim, Dr. Michael Thompson',
        type: 'Research Article',
        subject: 'Basic',
        journal: 'Biochemistry and Medical Science',
        year: '2024',
        language: 'en',
        link: 'https://drive.google.com/file/d/1VdqTYsIm1G5GWZ2Po1ebvDB02Uk8dM84/view',
        doi: '10.1234/basic.2024.006',
        abstract: 'Essential biochemistry concepts for medical students including metabolism and molecular biology.',
        keywords: ['biochemistry', 'metabolism', 'molecular biology', 'medical education'],
        category: 'Basic',
        uploadDate: '2024-02-10'
      },
      {
        id: 'ref_7',
        title: 'Primary Health Care: Advanced Topics',
        authors: 'Dr. Amina Hassan, Dr. Robert Clarke',
        type: 'Textbook',
        subject: 'PHC 3',
        journal: 'Community Health Publications',
        year: '2024',
        language: 'en',
        link: 'https://drive.google.com/file/d/1VdqTYsIm1G5GWZ2Po1ebvDB02Uk8dM85/view',
        doi: '10.1234/phc.2024.007',
        abstract: 'Advanced topics in primary health care delivery, community health assessment, and preventive medicine.',
        keywords: ['primary health care', 'community health', 'prevention', 'public health'],
        category: 'PHC 3',
        uploadDate: '2024-02-15'
      },
      {
        id: 'ref_8',
        title: 'Research Methodology in Medical Sciences',
        authors: 'Dr. Khalid Al-Mansouri, Dr. Emily Watson',
        type: 'Research Article',
        subject: 'Research project 1',
        journal: 'Journal of Medical Research',
        year: '2024',
        language: 'en',
        link: 'https://drive.google.com/file/d/1VdqTYsIm1G5GWZ2Po1ebvDB02Uk8dM86/view',
        doi: '10.1234/research.2024.008',
        abstract: 'Fundamentals of scientific research methodologies and their application in medical research.',
        keywords: ['research methodology', 'scientific research', 'medical research', 'methodology'],
        category: 'Research project 1',
        uploadDate: '2024-02-20'
      },
      {
        id: 'ref_9',
        title: 'Community Medicine and Health Promotion',
        authors: 'Dr. Nadia Mahmoud, Dr. Peter Adams',
        type: 'Clinical Guidelines',
        subject: 'PHC 3',
        journal: 'Public Health Guidelines',
        year: '2024',
        language: 'en',
        link: 'https://drive.google.com/file/d/1VdqTYsIm1G5GWZ2Po1ebvDB02Uk8dM87/view',
        doi: '10.1234/phc.2024.009',
        abstract: 'Evidence-based strategies for health promotion and disease prevention in community settings.',
        keywords: ['community medicine', 'health promotion', 'disease prevention', 'epidemiology'],
        category: 'PHC 3',
        uploadDate: '2024-02-25'
      },
      {
        id: 'ref_10',
        title: 'Scientific Writing and Publication',
        authors: 'Dr. Rania Fouad, Dr. James Wilson',
        type: 'Review',
        subject: 'Research project 1',
        journal: 'Academic Publishing Guide',
        year: '2024',
        language: 'en',
        link: 'https://drive.google.com/file/d/1VdqTYsIm1G5GWZ2Po1ebvDB02Uk8dM88/view',
        doi: '10.1234/research.2024.010',
        abstract: 'Guide to scientific writing, manuscript preparation, and publication process in medical journals.',
        keywords: ['scientific writing', 'publication', 'manuscript', 'academic writing'],
        category: 'Research project 1',
        uploadDate: '2024-03-01'
      }
    ];
  }

  // Get subjects for a batch
  async getSubjects(batch = '44') {
    const content = await this.getContent(batch);
    const subjects = [...new Set(content.map(item => item.subject))].sort();
    return subjects;
  }

  // Get content by subject
  async getContentBySubject(subject, batch = '44') {
    const content = await this.getContent(batch);
    return content.filter(item => item.subject === subject);
  }

  // Search content
  async searchContent(query, batch = '44') {
    const content = await this.getContent(batch);
    const searchTerm = query.toLowerCase();

    return content.filter(item =>
      item.title.toLowerCase().includes(searchTerm) ||
      item.subject.toLowerCase().includes(searchTerm) ||
      (item.description && item.description.toLowerCase().includes(searchTerm))
    );
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
  }

  // Get cache info
  getCacheInfo() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  // Utility methods for content processing
  extractYouTubeId(url) {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }

    return null;
  }

  convertGoogleDriveLink(url) {
    if (!url) return '';

    const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (fileIdMatch) {
      const fileId = fileIdMatch[1];
      return `https://drive.google.com/file/d/${fileId}/preview`;
    }

    return url;
  }

  // Get content statistics
  async getContentStats(batch = '44') {
    const content = await this.getContent(batch);

    const stats = {
      total: content.length,
      byType: {},
      bySubject: {},
      byLanguage: {}
    };

    content.forEach(item => {
      // By type
      stats.byType[item.type] = (stats.byType[item.type] || 0) + 1;

      // By subject
      stats.bySubject[item.subject] = (stats.bySubject[item.subject] || 0) + 1;

      // By language
      stats.byLanguage[item.language] = (stats.byLanguage[item.language] || 0) + 1;
    });

    return stats;
  }

  // ===== LAB DATA METHODS =====

  // Get labs for a specific subject
  async getLabsBySubject(subject) {
    const cacheKey = `labs_${subject}`;
    const cachedData = this.cache.get(cacheKey);

    console.log(`[LabsData] Fetching labs for subject: ${subject}`);

    if (cachedData && Date.now() - cachedData.timestamp < this.cacheTimeout) {
      console.log(`[LabsData] Using cached data for ${subject}:`, cachedData.data);
      return cachedData.data;
    }

    try {
      const url = `/data/labs/${subject}.json`;
      console.log(`[LabsData] Fetching from URL: ${url}`);
      const response = await fetch(url);

      if (!response.ok) {
        if (response.status === 404) {
          console.log(`[LabsData] No labs found for subject: ${subject} (404)`);
          return { subject, labs: [] };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const labData = await response.json();
      console.log(`[LabsData] Successfully loaded labs for ${subject}:`, labData);

      // Cache the data
      this.cache.set(cacheKey, {
        data: labData,
        timestamp: Date.now()
      });

      return labData;
    } catch (error) {
      console.error(`[LabsData] Error fetching labs for ${subject}:`, error);
      return { subject, labs: [] };
    }
  }

  // Get a specific lab by ID
  async getLabById(subject, labId) {
    try {
      const labData = await this.getLabsBySubject(subject);
      const lab = labData.labs?.find(lab => lab.id === labId);

      if (!lab) {
        throw new Error(`Lab with ID ${labId} not found in ${subject}`);
      }

      return lab;
    } catch (error) {
      console.error(`Error fetching lab ${labId} from ${subject}:`, error);
      throw error;
    }
  }

  // Get all available lab subjects
  async getLabSubjects() {
    try {
      // This would typically come from an index file or API
      // For now, return known subjects
      const knownSubjects = ['git1', 'git2', 'basic', 'phc3', 'research1'];

      const availableSubjects = [];

      for (const subject of knownSubjects) {
        try {
          const labData = await this.getLabsBySubject(subject);
          if (labData.labs && labData.labs.length > 0) {
            availableSubjects.push({
              subject,
              labCount: labData.labs.length,
              metadata: labData.metadata
            });
          }
        } catch (error) {
          // Subject file doesn't exist, skip it
          continue;
        }
      }

      return availableSubjects;
    } catch (error) {
      console.error('Error fetching lab subjects:', error);
      return [];
    }
  }

  // Search labs across all subjects
  async searchLabs(query, subject = null) {
    try {
      const searchTerm = query.toLowerCase();
      let subjects = [];

      if (subject) {
        subjects = [subject];
      } else {
        const availableSubjects = await this.getLabSubjects();
        subjects = availableSubjects.map(s => s.subject);
      }

      const results = [];

      for (const subj of subjects) {
        try {
          const labData = await this.getLabsBySubject(subj);
          const matchingLabs = labData.labs?.filter(lab => {
            return (
              lab.title.en.toLowerCase().includes(searchTerm) ||
              lab.title.ar.toLowerCase().includes(searchTerm) ||
              lab.author.toLowerCase().includes(searchTerm) ||
              lab.method.toLowerCase().includes(searchTerm) ||
              (lab.introduction.en && lab.introduction.en.toLowerCase().includes(searchTerm)) ||
              (lab.introduction.ar && lab.introduction.ar.toLowerCase().includes(searchTerm))
            );
          }) || [];

          matchingLabs.forEach(lab => {
            results.push({
              ...lab,
              subject: subj
            });
          });
        } catch (error) {
          console.error(`Error searching labs in ${subj}:`, error);
          continue;
        }
      }

      return results;
    } catch (error) {
      console.error('Error searching labs:', error);
      return [];
    }
  }

  // Get lab statistics
  async getLabStats() {
    try {
      const subjects = await this.getLabSubjects();

      let totalLabs = 0;
      const statsBySubject = {};
      const statsByDifficulty = { beginner: 0, intermediate: 0, advanced: 0 };

      for (const subjectInfo of subjects) {
        const labData = await this.getLabsBySubject(subjectInfo.subject);
        const labs = labData.labs || [];

        totalLabs += labs.length;
        statsBySubject[subjectInfo.subject] = labs.length;

        labs.forEach(lab => {
          if (lab.difficulty && statsByDifficulty.hasOwnProperty(lab.difficulty)) {
            statsByDifficulty[lab.difficulty]++;
          }
        });
      }

      return {
        totalLabs,
        totalSubjects: subjects.length,
        bySubject: statsBySubject,
        byDifficulty: statsByDifficulty
      };
    } catch (error) {
      console.error('Error getting lab statistics:', error);
      return {
        totalLabs: 0,
        totalSubjects: 0,
        bySubject: {},
        byDifficulty: { beginner: 0, intermediate: 0, advanced: 0 }
      };
    }
  }
}

export const dataService = new DataService();
